import { useGlobalNotification } from "@expert/common-ui";
import { getLogger } from "@expert/logging";
import { Group, Stack, Text } from "@mantine/core";
import { useEffect } from "react";
import sharedStyles from "@/common-ui/sharedStyles.module.css";
import { useVerifiedCustomerStore } from "@/customer-info";
import {
    type ActiveConferenceParticipant,
    type AgentSdk,
    type ConferenceParticipant,
    type Task,
    useSessionId,
    type VoiceTask,
} from "@/sdk";
import { classNames, useAsyncLoading } from "@/shared-utils";
import { HoldButton, RemoveParticipantButton } from "../../Buttons";
import { withControlsLock } from "../../controls.store";
import styles from "./ParticipantControls.module.css";
import { ParticipantLabel } from "./ParticipantLabel";

interface ConferenceParticipantControlsProps {
    participant: ConferenceParticipant;
    isPrimary?: boolean;
    isPending: boolean;
    task: VoiceTask;
    agentSdk: AgentSdk<Task>;
    holdButtonDisabled?: boolean;
}

export function ConferenceParticipantControls({
    participant,
    isPrimary = false,
    isPending,
    task,
    agentSdk,
    holdButtonDisabled,
}: ConferenceParticipantControlsProps) {
    const logger = getLogger({ module: "participantControls" });
    const globalNotification = useGlobalNotification();
    const sessionId = useSessionId();
    const { customer } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId));
    const participantError = !participant.participantName;

    const customerLabel = customer?.fullName ?? "Primary caller";

    const [loading, handleRemoveParticipant] = useAsyncLoading(
        withControlsLock(() => {
            if (isPending) {
                return agentSdk.removePendingParticipant(task, participant.participantCallId);
            } else {
                return agentSdk.removeParticipant(task, participant.participantCallId);
            }
        }),
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (participantError) {
            globalNotification.error(
                `We had trouble adding '${participant.participantCallId}' to the call.  Please check the number and try again.`,
            );
        }
    }, [participant.participantName]);

    if (participantError) {
        if (participant.participantCallId) {
            logger.error(
                { task },
                `Could not find participant with call ID ${participant.participantCallId}... removing from conference.`,
            );
            void handleRemoveParticipant();
        } else {
            logger.error(
                { task },
                `Could not find identifiers for the given participant... unable to remove from conference.`,
            );
        }
        return null;
    }

    const onHoldClick = (hold: boolean) =>
        agentSdk.updateParticipant(task, participant.participantCallId, {
            endConferenceOnExit: false,
            hold,
        });

    return (
        <Group
            classNames={{
                root: classNames(styles.participantContainer, isPrimary ? styles.primaryBackground : null),
            }}
            data-testid={isPending ? "pending-participant-panel" : "active-participant-controls"}
        >
            <Stack gap="1px">
                {isPending ? (
                    <Text size="xs" fw={300} className={sharedStyles.animatedEllipses}>
                        Calling
                    </Text>
                ) : null}
                {isPrimary ? (
                    <Group gap="0.3rem">
                        <Text size="xs" c="white">
                            {customerLabel}
                        </Text>
                    </Group>
                ) : null}
                <Group gap="xs">
                    <ParticipantLabel
                        mdn={participant.participantName}
                        quickConnectName={participant.quickConnectName}
                    />
                </Group>
            </Stack>
            <span className={sharedStyles.stretch} />
            <>
                {isPending ? null : (
                    <HoldButton
                        task={task}
                        outlined
                        isOnHold={!!participant.hold}
                        onPauseCallButtonClick={() => onHoldClick(true)}
                        onResumeCallButtonClick={() => onHoldClick(false)}
                        participant={participant as ActiveConferenceParticipant}
                        isDisabled={holdButtonDisabled}
                    />
                )}
                <RemoveParticipantButton onClick={handleRemoveParticipant} disabled={!!participant.hold || loading} />
            </>
        </Group>
    );
}
